import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'components/Skeleton';
import { useAnalytics } from 'hooks/useAnalytics';
import { classNames, formatterGbp } from 'utils';

import { useBatchedRatings } from '../hooks/useWineDetail';
import { CurrentInvest } from '../components/CurrentInvest';
import { InvestOfferProps } from '../components/InvestPromotion';
import { InvestOffer } from '../types';
import { RowRatings, getRowRatings, buildRows } from './rowQueries';
import { fetchActiveMarkets } from '../../Trade/services/fetchActiveMarkets1';
import { fetchActiveMarketsFilters } from '../../Trade/services/fetchActiveMarketsFilters';
import { OffersType, FilterConfigType, MARKET_REFS } from '../../Trade/types';
import FilterView, { FiltersType, FilterViewRef } from '../../Trade/components/SlideOutView/FilterView';
import { applyFilters, priceRangeFilters } from '../../Trade/helpers';
import { handleImageError } from 'helpers';

interface ForYouChromeProps {
  onItemSelect: InvestOfferProps['onItemSelect'];
}

type SubTab = 'for-you' | 'strategies' | 'parcels' | 'all-offers';

const TABS: { value: SubTab; label: string }[] = [
  { value: 'for-you', label: 'For You' },
  // { value: 'strategies', label: 'Strategies' },
  // { value: 'parcels', label: 'Parcels' },
  { value: 'all-offers', label: 'All offers' },
];

// ────────────────────────────────────────────────────────────────────────────
// Chrome
// ────────────────────────────────────────────────────────────────────────────

const ForYouChrome: FC<ForYouChromeProps> = ({ onItemSelect }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as SubTab | null;
  const activeTab: SubTab = TABS.find((t) => t.value === tabParam)?.value ?? 'for-you';

  const setTab = (t: SubTab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', t);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="h-full overflow-y-auto pt-2 px-5 md:px-8 pb-10">
      <div
        role="tablist"
        aria-label="Invest sections"
        className="flex gap-7 border-b border-stone dark:border-slate-700 mb-5 overflow-x-auto"
      >
        {TABS.map((tab) => {
          const isActive = activeTab === tab.value;
          return (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setTab(tab.value)}
              className={classNames(
                'pb-3 -mb-px text-14 whitespace-nowrap transition-colors',
                isActive
                  ? 'text-vine dark:text-white font-semibold border-b-2 border-orange'
                  : 'text-gray-500 dark:text-gray-400 hover:text-vine dark:hover:text-white',
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      {activeTab === 'for-you' && <ForYouHub onItemSelect={onItemSelect} />}
      {activeTab === 'strategies' && (
        <Placeholder
          title="Strategies"
          body="Active investment strategies (Alpha, EP) are coming soon — campaigns with a thesis, deployment window, and soft-reserve commitments."
        />
      )}
      {activeTab === 'parcels' && (
        <Placeholder
          title="Parcels"
          body="Curated bundles sold at tier prices are coming soon — producer horizontals, regional sets, and themed collections."
        />
      )}
      {activeTab === 'all-offers' && <CurrentInvest onItemSelect={onItemSelect} />}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// CSS Variables for the design system
// ────────────────────────────────────────────────────────────────────────────

const cssVars = {
  '--ink': '#1a1a1a',
  '--line': '#e5e0d8',
  '--mute': '#888',
  '--good': '#2e7d32',
  '--coral': '#e85a4f',
  '--teal': '#1a3c40',
  '--note': '#fffbe6',
  '--noteline': '#d4b106',
} as const;

// ────────────────────────────────────────────────────────────────────────────
// Market Opportunity Types for API calls
// ────────────────────────────────────────────────────────────────────────────

// Row key IDs matching buildRows output
type RowKeyId =
  | 'under-market'
  | 'new-releases'
  | 'allocations'
  | 'great-fair-value'
  | 'curated-parcels'
  | 'iconic-wines';

interface RowDefinition {
  id: RowKeyId;
  title: string;
  subtitle: string;
  cap: number;
}

const ROW_DEFINITIONS: RowDefinition[] = [
  {
    id: 'under-market',
    title: 'Under-Market Opportunities',
    subtitle: 'Chosen for you, based on your portfolio',
    cap: 6,
  },
  {
    id: 'new-releases',
    title: 'New Releases',
    subtitle: 'Just landed',
    cap: 8,
  },
  {
    id: 'allocations',
    title: 'Allocations',
    subtitle: 'Limited supply · You qualify',
    cap: 6,
  },
  {
    id: 'great-fair-value',
    title: 'Great Fair Value',
    subtitle: 'Compelling, accessible price points',
    cap: 8,
  },
  {
    id: 'curated-parcels',
    title: 'Curated Parcels',
    subtitle: 'Bundled collections',
    cap: 6,
  },
  {
    id: 'iconic-wines',
    title: 'Iconic Wines',
    subtitle: 'The trophies',
    cap: 8,
  },
];

// ────────────────────────────────────────────────────────────────────────────
// Transform OffersType to InvestOffer
// ────────────────────────────────────────────────────────────────────────────

function transformToInvestOffer(offer: OffersType): InvestOffer {
  return {
    // InvestOffer specific fields
    id: offer.id,
    type: offer.orderType,
    name: offer.wineName,
    subtitle: `${offer.vintage} · ${offer.unitSize}`,
    priceGbp: offer.priceGbp ?? offer.price,
    unitSize: offer.unitSize,
    expiryDate: offer.createdDateTime,
    region: offer.region,
    status: 'current',
    offerSource: 'LD',
    mainImage: `https://cwcdst2prdctimgimprtfnct.blob.core.windows.net/height/400/${offer.lwin18?.substring(0, 7)}.png`,
    sections: [],
    disclaimer: '',
    // CampaignsOffersType required fields
    buyOrSell: offer.buyOrSell as 'B' | 'S',
    price: offer.priceInTargetCurrency ?? offer.price,
    initialQuantity: offer.initialQuantity,
    quantityOutstanding: offer.quantityOutstanding,
    stopLimitPrice: offer.stopLimitPrice,
    orderType: offer.orderType,
    wineName: offer.wineName,
    vintage: offer.vintage,
    assetId: offer.assetId,
    lwin18: offer.lwin18,
    userId: offer.userId,
    assetAccountId: offer.assetAccountId,
    createdDateTime: offer.createdDateTime,
    updatedDateTime: offer.updatedDateTime,
    marketValue: offer.marketValueGbp ?? offer.marketValue,
    inPackage: offer.inPackage,
    highestBid: typeof offer.highestBid === 'object' ? offer.highestBid?.price ?? null : null,
    lowestOffer: typeof offer.lowestOffer === 'object' ? offer.lowestOffer?.price ?? null : null,
    campaignName: offer.campaignName ?? '',
    campaignId: Number(offer.campaignId) || 0,
    packageName: offer.packageName,
    packageId: offer.packageId,
    executionPrice: offer.executionPrice,
    wac: Number(offer.wac) || 0,
    physicalQuantity: offer.physicalQuantity,
    orderCreatedDateTime: offer.createdDateTime,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Constants and helpers
// ────────────────────────────────────────────────────────────────────────────

const FETCH_PAGE_SIZE = 100; // Fetch 100 rows to filter client-side

// Helper to get price range for filtering
function getPriceRange(price: number): string {
  if (price < 500) return 'Under £500';
  if (price < 1000) return 'Under £1000';
  if (price >= 1000 && price < 2000) return '£1k-£2k';
  if (price >= 2000 && price < 5000) return '£2k-£5k';
  if (price >= 5000 && price < 10000) return '£5k-£10k';
  if (price >= 10000) return '£10k+';
  return '';
}

// ────────────────────────────────────────────────────────────────────────────
// For You hub (6 rows) - New design with PortalActiveMarkets API
// ────────────────────────────────────────────────────────────────────────────

const ForYouHub: FC<{ onItemSelect: InvestOfferProps['onItemSelect'] }> = ({ onItemSelect }) => {
  const { t } = useTranslation();
  const filterViewRef = useRef<FilterViewRef>(null);

  // Filter state
  const [filterSlideOutConfig, setFilterSlideOutConfig] = useState<FilterConfigType>({
    filters: null,
    onBack: () => {},
    isFiltered: false,
  });

  // Custom filters configuration
  const customFilters = useMemo(
    () => ({
      [MARKET_REFS.REGION]: {
        label: t('trade:dropDownFilters.title.region'),
        selectAllText: t('trade:dropDownFilters.value.region'),
        selected: [],
        previewSelected: [],
      },
      [MARKET_REFS.PRODUCER]: {
        label: t('trade:dropDownFilters.title.producer'),
        selectAllText: t('trade:dropDownFilters.value.producer'),
        selected: [],
        previewSelected: [],
      },
      [MARKET_REFS.VINTAGE]: {
        label: t('trade:dropDownFilters.title.vintage'),
        selectAllText: t('trade:dropDownFilters.value.vintage'),
        selected: [],
        previewSelected: [],
        isNumerical: true,
      },
      [MARKET_REFS.UNITSIZE]: {
        label: t('trade:dropDownFilters.title.size'),
        selectAllText: t('trade:dropDownFilters.value.size'),
        selected: [],
        previewSelected: [],
        isNumerical: true,
      },
      [MARKET_REFS.PRICE_RANGE]: {
        label: t('trade:dropDownFilters.title.price'),
        selectAllText: t('trade:dropDownFilters.value.price'),
        selected: [],
        previewSelected: [],
        isAsc: true,
        customSortOrder: ['Under £500', 'Under £1000', '£1k-£2k', '£2k-£5k', '£5k-£10k', '£10k+'],
      },
    }),
    [t],
  );

  // First fetch all offers to get lwin11s for ratings
  const { data: rawData, isLoading: rawLoading } = useQuery({
    queryKey: ['portalActiveMarkets', 'all', FETCH_PAGE_SIZE],
    queryFn: () =>
      fetchActiveMarkets({
        page: 0,
        pageSize: FETCH_PAGE_SIZE,
      }),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  // Transform raw offers for lwin11 extraction
  const rawOffers = useMemo(() => {
    if (!rawData?.data) return [];
    return rawData.data.map(transformToInvestOffer);
  }, [rawData]);

  // Get lwin11s for ratings batch fetch
  const lwin11sForRatings = useMemo(
    () => rawOffers.map((o) => (o.lwin18 ? o.lwin18.substring(0, 11) : '')).filter(Boolean),
    [rawOffers],
  );

  // Fetch ratings first so we can use them in buildRows
  const { data: ratingsMap, isLoading: ratingsLoading } = useBatchedRatings(lwin11sForRatings);

  // Build rows with filtering/sorting using ratings data
  const builtRows = useMemo(() => {
    return buildRows(rawOffers, ratingsMap);
  }, [rawOffers, ratingsMap]);

  // Apply user filters to each row
  const rowData = useMemo(() => {
    const result = {} as Record<RowKeyId, { offers: InvestOffer[]; allOffers: InvestOffer[] }>;

    for (const def of ROW_DEFINITIONS) {
      const rowOffers = builtRows[def.id] || [];

      let filteredOffers = rowOffers;
      if (filterSlideOutConfig.isFiltered && filterSlideOutConfig.filters) {
        // Apply user filters to the row's offers
        const mappedOffers = rowOffers.map((o) => ({
          ...o,
          priceRange: getPriceRange(o.priceGbp),
        }));
        const filteredDataSource = applyFilters(mappedOffers, filterSlideOutConfig.filters);
        filteredOffers = (filteredDataSource?.filteredDataSource || []) as unknown as InvestOffer[];
      }

      result[def.id] = {
        offers: filteredOffers.slice(0, def.cap),
        allOffers: rowOffers,
      };
    }

    return result;
  }, [builtRows, filterSlideOutConfig]);

  // Filter source data for FilterView - use unfiltered offers to keep dropdown options stable
  const filterSourceData = useMemo(() => {
    const allRowOffers = Object.values(rowData).flatMap((r) => r.allOffers);
    // Add priceRange for filter dropdown
    const withPriceRange = allRowOffers.map((o) => ({
      ...o,
      priceRange: getPriceRange(o.priceGbp),
    }));
    return [...withPriceRange, ...priceRangeFilters];
  }, [rowData]);

  // Collect all offers for display
  const allOffers = useMemo(() => {
    return Object.values(rowData).flatMap((r) => r.offers);
  }, [rowData]);

  const isLoading = rawLoading || (lwin11sForRatings.length > 0 && ratingsLoading && ratingsMap.size === 0);

  const { track } = useAnalytics();

  // Spec: hide rows with fewer than 3 eligible offers; emit row_hidden_no_eligible for telemetry.
  const hiddenRows = useMemo(
    () => ROW_DEFINITIONS.filter((def) => rowData[def.id]?.offers.length < 3 && !isLoading),
    [rowData, isLoading],
  );

  useEffect(() => {
    if (isLoading || allOffers.length === 0) return;
    // visibleRows.forEach((def) =>
    //   track('row_impression', { source_row: def.id, eligible_count: rowData[def.id].offers.length }),
    // );
    hiddenRows.forEach((def) => track('row_hidden_no_eligible', { source_row: def.id }));
  }, [isLoading, allOffers.length, hiddenRows, rowData, track]);

  // Handle filter change from FilterView
  const handleFilterChange = (isFiltered: boolean, filters: FiltersType<unknown> | null) => {
    setFilterSlideOutConfig((prev) => ({
      ...prev,
      isFiltered,
      filters,
    }));
  };

  if (isLoading && allOffers.length === 0) {
    return <HubSkeleton rowCount={3} cardsPerRow={4} />;
  }

  // Check if any row has results (considering filter state)
  const hasAnyResults = Object.values(rowData).some((r) => r.offers.length > 0);

  if (!hasAnyResults && !isLoading) {
    return (
      <div className="space-y-6" style={cssVars as React.CSSProperties}>
        {/* Keep filters visible so user can clear them */}
        <div className="mb-5">
          <FilterView
            ref={filterViewRef}
            isServerSide={true}
            customFilters={customFilters}
            sourceData={filterSourceData}
            setFilteredSource={handleFilterChange}
          />
        </div>
        <div
          className="border border-dashed rounded-lg p-8 text-center"
          style={{ borderColor: cssVars['--line'], color: cssVars['--mute'] }}
        >
          <div className="text-sm">
            {filterSlideOutConfig.isFiltered
              ? 'No offers match your current filters. Try adjusting or clearing your filters.'
              : 'No offers match the personalised rows right now. New offers land daily — check back tomorrow, or browse '}
            {!filterSlideOutConfig.isFiltered && <em>All offers</em>}
            {!filterSlideOutConfig.isFiltered && '.'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" style={cssVars as React.CSSProperties}>
      {/* Filters Row */}
      <div className="mb-5">
        <FilterView
          ref={filterViewRef}
          isServerSide={true}
          customFilters={customFilters}
          sourceData={filterSourceData}
          setFilteredSource={handleFilterChange}
        />
      </div>

      {/* Carousel Rows */}
      {ROW_DEFINITIONS.map((def) => {
        const rowInfo = rowData[def.id];
        if (!rowInfo) return null;
        const { offers } = rowInfo;
        // Show skeleton while loading
        if (isLoading && offers.length === 0) {
          return <RowSkeleton key={def.id} cards={Math.min(def.cap, 4)} />;
        }
        // When filters are applied, show rows with any results; otherwise hide rows with < 3 offers
        const minOffers = filterSlideOutConfig.isFiltered ? 1 : 3;
        if (offers.length < minOffers) {
          return null;
        }
        return <Carousel key={def.id} def={def} offers={offers} ratingsMap={ratingsMap} onItemSelect={onItemSelect} />;
      })}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Carousel Component (replaces Row)
// ────────────────────────────────────────────────────────────────────────────

const Carousel: FC<{
  def: RowDefinition;
  offers: InvestOffer[];
  ratingsMap?: Map<string, RowRatings>;
  onItemSelect: InvestOfferProps['onItemSelect'];
}> = ({ def, offers, ratingsMap, onItemSelect }) => {
  const { track } = useAnalytics();
  const trackRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (!trackRef.current) return;
    const scrollAmount = 230; // card width + gap
    trackRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  return (
    <section className="mb-8" aria-labelledby={`row-${def.id}-title`}>
      {/* Carousel Header */}
      <div className="flex items-baseline justify-between mb-2.5">
        <h3 id={`row-${def.id}-title`} className="text-base font-semibold" style={{ color: cssVars['--ink'] }}>
          {def.title}
          <small className="font-normal ml-2 text-xs" style={{ color: cssVars['--mute'] }}>
            · {def.subtitle}
          </small>
        </h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => scroll('left')}
            className="w-7 h-7 rounded-full border border-gray-300 bg-white flex items-center justify-center text-sm hover:bg-gray-50"
            aria-label="Scroll left"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={() => scroll('right')}
            className="w-7 h-7 rounded-full border border-gray-300 bg-white flex items-center justify-center text-sm hover:bg-gray-50"
            aria-label="Scroll right"
          >
            ›
          </button>
          <button
            type="button"
            className="text-xs ml-2 hover:underline"
            style={{ color: cssVars['--coral'] }}
            onClick={() => track('view_all_click', { source_row: def.id })}
          >
            View all →
          </button>
        </div>
      </div>

      {/* Carousel Track */}
      <div
        ref={trackRef}
        className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scroll-smooth"
        style={{ scrollbarWidth: 'thin', scrollSnapType: 'x mandatory' }}
      >
        {offers.map((offer, idx) => (
          <CarouselCard
            key={offer.id}
            offer={offer}
            ratingsMap={ratingsMap}
            onClick={() => {
              track('card_open', { source_row: def.id, wine_id: offer.lwin18, card_position: idx });
              onItemSelect(offer, undefined, def.id);
            }}
          />
        ))}
      </div>
    </section>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Carousel Card Component
// ────────────────────────────────────────────────────────────────────────────

const CarouselCard: FC<{
  offer: InvestOffer;
  ratingsMap?: Map<string, RowRatings>;
  onClick: () => void;
}> = ({ offer, ratingsMap, onClick }) => {
  const [imgError, setImgError] = useState(false);
  const lwin11 = offer.lwin18 ? offer.lwin18.substring(0, 11) : '';
  const ratings = ratingsMap?.get(lwin11) ?? getRowRatings(offer.lwin18);
  const discount =
    offer.marketValue && offer.marketValue > 0 && offer.priceGbp < offer.marketValue
      ? ((offer.marketValue - offer.priceGbp) / offer.marketValue) * 100
      : null;

  // Generate "why" text based on wine attributes
  const whyText = useMemo(() => {
    const reasons: string[] = [];
    if (ratings.liquidity >= 93) reasons.push(`Liquidity ${(ratings?.liquidity ?? 0).toFixed(0)}`);
    if (ratings.grade >= 8) reasons.push(`Grade ${(ratings?.grade ?? 0).toFixed(0)}`);
    if (offer.priceGbp < 1000) reasons.push('Under £1k');
    return reasons.length > 0 ? reasons.join(' · ') : 'Chosen for you';
  }, [ratings, offer.priceGbp]);

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex-none flex flex-col gap-2 p-3 rounded-md bg-white text-left transition-transform hover:-translate-y-0.5"
      style={{
        width: 210,
        minHeight: 300,
        border: '1px solid #d6d2cc',
        scrollSnapAlign: 'start',
      }}
    >
      {/* Bottle Image Area */}
      <div
        className="relative h-32 rounded flex items-center justify-center text-xs"
        style={{
          background: 'linear-gradient(180deg, #f0ebe2, #e6dfd2)',
          border: '1px dashed #bbb',
          color: '#999',
        }}
      >
        {offer?.mainImage ? (
          <img
            src={offer.mainImage}
            alt={`${offer.name} ${offer.vintage}`}
            className="max-h-full max-w-full object-contain"
            onError={handleImageError}
          />
        ) : (
          <div className="flex flex-col items-center justify-center text-center p-2">
            <svg className="w-12 h-12 mb-1 opacity-40" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 3C10.34 3 9 4.34 9 6v6c0 .55.45 1 1 1h4c.55 0 1-.45 1-1V6c0-1.66-1.34-3-3-3zm2 9h-4V6c0-1.1.9-2 2-2s2 .9 2 2v6zm-2 3c-.55 0-1 .45-1 1v4h2v-4c0-.55-.45-1-1-1z" />
              <path d="M17 8v4c0 2.76-2.24 5-5 5s-5-2.24-5-5V8H5v4c0 3.53 2.61 6.43 6 6.92V21h2v-2.08c3.39-.49 6-3.39 6-6.92V8h-2z" />
            </svg>
            <span className="text-xs opacity-60">No image</span>
          </div>
        )}
        {/* Grade Badge */}
        <span
          className="absolute top-2 left-2 text-white text-xs font-semibold px-1.5 py-0.5 rounded"
          style={{ backgroundColor: cssVars['--good'], fontSize: 10 }}
        >
          {(ratings?.critic ?? 0).toFixed(0)}
        </span>
        {/* Region Badge */}
        {offer.region && (
          <span
            className="absolute top-2 right-2 bg-white text-xs px-1.5 py-0.5 rounded"
            style={{ border: '1px solid #ddd', fontSize: 10 }}
          >
            {offer.region}
          </span>
        )}
      </div>

      {/* Wine Name */}
      <div className="text-xs font-semibold leading-tight line-clamp-2" style={{ fontSize: 12.5 }}>
        {offer.name}
        {offer.vintage ? ` ${offer.vintage}` : ''}
      </div>

      {/* Meta Info */}
      <div className="text-xs" style={{ color: cssVars['--mute'], fontSize: 11 }}>
        {offer.unitSize}
        {offer.region ? ` · ${offer.region}` : ''}
      </div>

      {/* Price Row */}
      <div className="flex justify-between items-baseline text-xs" style={{ fontSize: 11 }}>
        <div>
          <span>Offer</span>
          <br />
          <strong className="text-sm">{formatterGbp.format(offer.priceGbp)}</strong>
        </div>
        {discount != null && (
          <div className="text-right font-semibold" style={{ color: cssVars['--good'] }}>
            −{discount.toFixed(1)}%
            <br />
            <span className="font-normal text-xs" style={{ color: cssVars['--mute'], fontSize: 10 }}>
              vs market
            </span>
          </div>
        )}
      </div>

      {/* Why Text */}
      <div
        className="text-xs italic pt-1.5 mt-auto"
        style={{ borderTop: '1px dashed #eee', color: '#888', fontSize: 10 }}
      >
        {whyText}
      </div>

      {/* CTA Button */}
      <div
        className="text-center py-1.5 rounded font-semibold text-white"
        style={{ backgroundColor: cssVars['--coral'], fontSize: 11.5 }}
      >
        View offer
      </div>
    </button>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Skeletons — mirror the new Carousel + CarouselCard layout
// ────────────────────────────────────────────────────────────────────────────

const OfferCardSkeleton: FC = () => (
  <div
    className="flex-none flex flex-col gap-2 p-3 rounded-md bg-white"
    style={{ width: 210, minHeight: 300, border: '1px solid #d6d2cc' }}
  >
    <Skeleton height={128} className="w-full rounded" />
    <Skeleton height={16} className="w-3/4" />
    <Skeleton height={12} className="w-1/2" />
    <div className="flex items-baseline justify-between mt-1">
      <div className="space-y-1">
        <Skeleton height={10} width={32} />
        <Skeleton height={16} width={70} />
      </div>
      <Skeleton height={16} width={50} />
    </div>
    <Skeleton height={10} className="w-full mt-auto" />
    <Skeleton height={28} className="w-full rounded" />
  </div>
);

const RowSkeleton: FC<{ cards: number }> = ({ cards }) => (
  <section className="mb-8">
    <div className="flex items-baseline justify-between mb-2.5">
      <Skeleton height={20} width={260} />
      <div className="flex items-center gap-2">
        <Skeleton height={28} width={28} className="rounded-full" />
        <Skeleton height={28} width={28} className="rounded-full" />
        <Skeleton height={14} width={60} className="ml-2" />
      </div>
    </div>
    <div className="flex gap-3 overflow-hidden pb-2">
      {Array.from({ length: cards }, (_, i) => (
        <OfferCardSkeleton key={i} />
      ))}
    </div>
  </section>
);

const HubSkeleton: FC<{ rowCount: number; cardsPerRow: number }> = ({ rowCount, cardsPerRow }) => (
  <div className="space-y-6">
    {/* Filter Pills Skeleton */}
    <div className="flex gap-1.5 flex-wrap mb-5">
      {Array.from({ length: 7 }, (_, i) => (
        <Skeleton key={i} height={24} width={70} className="rounded-full" />
      ))}
    </div>
    {/* Row Skeletons */}
    {Array.from({ length: rowCount }, (_, i) => (
      <RowSkeleton key={i} cards={cardsPerRow} />
    ))}
  </div>
);

// ────────────────────────────────────────────────────────────────────────────
// Placeholder for Strategies / Parcels tabs
// ────────────────────────────────────────────────────────────────────────────

const Placeholder: FC<{ title: string; body: string }> = ({ title, body }) => (
  <div className="border border-dashed border-stone dark:border-slate-700 rounded-lg p-10 text-center">
    <div className="text-20 font-medium text-vine dark:text-white">{title}</div>
    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 max-w-md mx-auto">{body}</p>
  </div>
);

export default ForYouChrome;
